import debounce from 'lodash.debounce';
import { ReactElement, RefObject, useMemo, useState } from 'react';

import Button from '../Button';
import style from './Carousel.module.scss';

interface ArrowProps {
  ariaLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

interface CarouselProps<T> {
  carouselRef: RefObject<HTMLDivElement | null>;
  data: T[] | null;
  extraChild?: ReactElement | null;
  leftArrow: ArrowProps;
  renderItem: (item: T, index: unknown) => ReactElement;
  rightArrow: ArrowProps;
}

const Carousel = <T, >({
  data,
  carouselRef,
  leftArrow,
  rightArrow,
  extraChild = null,
  renderItem,
}: CarouselProps<T>) => {
  const [isScrolledRight, setIsScrolledRight] = useState(false);

  const setIsScrolledRightDebounce = useMemo(
    () => debounce((isScrolledRight: boolean) => setIsScrolledRight(isScrolledRight), 16),
    [setIsScrolledRight]
  );

  return (
    <div className={style.carousel}>
      <Button
        ariaLabel={leftArrow.ariaLabel}
        className={style.arrowButton}
        disabled={!isScrolledRight || leftArrow.disabled}
        icon={{ name: 'arrow_back_ios' }}
        onClick={leftArrow.onClick}
      />
      <div
        className={style.contentItems}
        onScroll={((event) => {
          setIsScrolledRightDebounce(event.currentTarget.scrollLeft !== 0);
        })}
        ref={carouselRef}
        role="tablist"
        tabIndex={0}
      >
        {!!data && !!data.length && data.map((item, index) => renderItem(item, index))}
        {extraChild}
      </div>
      <Button
        ariaLabel={rightArrow.ariaLabel}
        className={style.arrowButton}
        disabled={rightArrow.disabled}
        icon={{ name: 'arrow_forward_ios' }}
        isLoading={rightArrow.isLoading}
        onClick={rightArrow.onClick}
      />
    </div>
  );
};

export default Carousel;
